import React, { forwardRef, useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  Avatar,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
  Paper,
  Slide,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import { TransitionProps } from "@mui/material/transitions";
import { theme } from "../../../../theme";
import { panelAndStakeholdersDropdown } from "./PanelStakeholders";
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import ParticipantLabel from "./ParticipantLabel";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="left" ref={ref} {...props} />;
});


interface AddorEditPanelAndStakeholderProps {
  open: boolean;
  onClose: () => void;
  formData: {
    username: string;
    email: string;
    role: null | number
  };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  onSave: () => void;
  panelAndStakeholders: panelAndStakeholdersDropdown[];
  mode: "add" | "edit"
}

const AddorEditPanelAndStakeholder: React.FC<AddorEditPanelAndStakeholderProps> = ({
  open,
  onClose,
  formData,
  setFormData,
  onSave,
  panelAndStakeholders,
  mode
}) => {

  const [formErrors, setFormErrors] = useState({
    username: '',
    email: '',
    role: '',
  });

  useEffect(() => {
    setFormErrors({
      username: '',
      email: '',
      role: '',
    })
  }, [open])

  const handleChange = (field: string, value: string | number) => {
    setFormData({ ...formData, [field]: value });
    setFormErrors({ ...formErrors, [field]: '' }); // Clear the error when user types
  };

  const validateForm = () => {
    const errors: any = {};

    if (!formData.username.trim()) {
      errors.username = "Username is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else {
      // Simple email regex pattern
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = "Enter a valid email address";
      }
    }

    if (!formData.role) {
      errors.role = "Role is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };


  const handleSave = () => {
    if (validateForm()) {
      onSave();
    }
  };




  return (
    <Dialog
      open={open}
      onClose={() => {}}
      TransitionComponent={Transition}
      fullScreen
      BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.5)",}}}
      PaperProps={{
        sx: {
          width: '22vw',

          height: '100vh',
          margin: 0,
          overflow: 'hidden',
          position: 'absolute',
          right: 0,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start', // aligns icon & title to the top
          justifyContent: 'space-between',
          p: '1.2vw 1vw 0 1vw',
          minHeight: '4vw',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {mode === "add" ? (
            <PersonAddAltOutlinedIcon
              sx={{
                border: '0.07vw solid #EEEEEE',
                bgcolor: theme.palette.secondary.light,
                color: theme.palette.secondary.main,
                borderRadius: '0.56vw',
                width: '2.5vw',
                height: '2.5vw',
                p: '0.3vw',
                mr: '1vw',
              }}
            />
          ) : (
            <CreateOutlinedIcon
              sx={{
                border: '0.07vw solid #EEEEEE',
                bgcolor: theme.palette.secondary.light,
                color: theme.palette.secondary.main,
                borderRadius: '0.56vw',
                width: '2.5vw',
                height: '2.5vw',
                p: '0.3vw',
                mr: '1vw',
              }}
            />
          )}

          <Typography variant="h6" fontWeight={700}>
            {mode === 'edit' ? 'Edit Participant' : 'Add Participant'}
          </Typography>
        </Box>

        <IconButton
          edge="end"
          onClick={onClose}
          aria-label="close"
          sx={{
            padding: '0.3vw',
            mt: '0.1vw',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>


      <Divider sx={{ mb: 1.2, mt: 2 }} />

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '1.6vw', padding: '1.2vw 1vw' }}>
        <TextField
          size="small"
          label="Username"
          name="username"
          value={formData.username}
          onChange={e => handleChange('username', e.target.value)}
          error={!!formErrors.username}
          helperText={formErrors.username}
          fullWidth
          required
        />

        <TextField
          size="small"
          label="Email Address"
          name="email"
          value={formData.email}
          onChange={e => handleChange('email', e.target.value)}
          error={!!formErrors.email}
          helperText={formErrors.email}
          fullWidth
          required
        />

        <FormControl fullWidth  required size="small" error={!!formErrors.role}>
          <InputLabel>Role</InputLabel>
          <Select
            label="Role"
            value={formData.role ?? ''}
            onChange={e => handleChange('role', e.target.value)}
          >
            {panelAndStakeholders.map(option => (
              <MenuItem key={option.RoleNum} value={option.RoleNum}>
                <ParticipantLabel role={option.RoleKey} />
              </MenuItem>
            ))}
          </Select>
          {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
        </FormControl>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ padding: '1.2vw 1vw' }}>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          fullWidth
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddorEditPanelAndStakeholder;